import { generatePath, Link } from "react-router-dom";

import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Pagination,
  Tooltip,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { RemoveCircleOutline } from "@mui/icons-material";

import nameof from "ts-nameof.macro";

import { dateToStringShort } from "../../../common/utils/dateTime";
import { formatNotaryData } from "../../../common/utils/formatters";
import AlertExtended from "../../../common/components/mui-extended/AlertExtended";
import CheckboxExtended from "../../../common/components/mui-extended/CheckboxExtended";
import DatePickerExtended from "../../../common/components/mui-extended/DatePickerExtended";
import SectionExtended from "../../../common/components/mui-extended/SectionExtended";
import SelectExtended from "../../../common/components/mui-extended/SelectExtended";
import TextFieldExtended from "../../../common/components/mui-extended/TextFieldExtended";
import { PublicRoutesConfig } from "../../RoutesConfig";
import useUserContext from "../../../hooks/useUserContext";
import { Rules } from "../../../common/validationRules";
import notaryService from "../../../services/notaryService";
import vehicleContractService from "../../../services/vehicleContractService";
import { PageDefaults } from "../../../common/interfaces/PageDto";
import { VehicleContractSearchDto, VehicleContractSearchResultDto } from "../../../interfaces/VehicleContract";
import { useAxiosLoader } from "../../../common/hooks/useAxiosLoader";
import { useSearchForm } from "../../../common/hooks/useSearchForm";
import TableSkeleton from "../../../common/components/TableSkeleton";

export default function Search() {
  const loading = useAxiosLoader(false);
  const { userId } = useUserContext();

  const defaultModel: VehicleContractSearchDto = {
    ...PageDefaults,
    userId: userId,
    isCancelled: false,
  };

  const form = useSearchForm<VehicleContractSearchDto, VehicleContractSearchResultDto>({
    defaultModel: defaultModel,
    rootSearchPath: PublicRoutesConfig.Vehicle.Search.Path,
    searchAsync: async (query: string) => {
      await vehicleContractService.getVehicleContractsAsync(query).then(async ({ data }) => {
        await vehicleContractService.getVehicleContractsCountAsync(query).then(({ data: count }) => {
          form.setResults(data, count);
        });
      });
    },
  });

  const renderForm = () => {
    return (
      <form onReset={form.onReset} onSubmit={form.onSubmit}>
        <Stack spacing={3}>
          <SectionExtended title="Данни за договори МПС">
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <SelectExtended
                  label="Нотариус"
                  name={nameof(defaultModel.notaryId)}
                  form={form}
                  searchRequest={notaryService.getNotaries}
                  initRequest={notaryService.getNotariesByIds}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextFieldExtended
                  label="Номер на договор"
                  name={nameof(defaultModel.registrationNumber)}
                  form={form}
                  rules={Rules.Number}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePickerExtended label="Дата" name={nameof(defaultModel.issueDate)} form={form} rules={Rules.Date} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextFieldExtended label="УРИ на преписка" name={nameof(defaultModel.caseURI)} form={form} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextFieldExtended
                  label="Регистрационен номер на ППС"
                  name={nameof(defaultModel.vehicleRegistrationNumber)}
                  form={form}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextFieldExtended
                  label="Рама на ППС"
                  name={nameof(defaultModel.vehicleIdentificationNumber)}
                  form={form}
                />
              </Grid>
              <Grid item xs={12}>
                <CheckboxExtended label="Прекратени / Анулирани" name={nameof(defaultModel.isCancelled)} form={form} />
              </Grid>
            </Grid>
          </SectionExtended>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Button startIcon={<RemoveCircleOutline />} type="reset">
                Изчисти
              </Button>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1}>
                <LoadingButton
                  variant="contained"
                  loading={loading}
                  loadingPosition="start"
                  startIcon={PublicRoutesConfig.Vehicle.Search.Icon}
                  type="submit"
                >
                  {PublicRoutesConfig.Vehicle.Search.Label}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </form>
    );
  };

  const renderTable = () => {
    return loading ? (
      <TableSkeleton />
    ) : form.results && form.results.length > 0 ? (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ verticalAlign: "top" }}>
                <TableCell>Нотариус</TableCell>
                <TableCell width="10%">Номер на договор</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell>УРИ на преписка</TableCell>
                <TableCell width="15%">Регистрационен номер на ППС</TableCell>
                <TableCell>Рама на ППС</TableCell>
                <TableCell width="45px"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {form.results.map((item) => (
                <TableRow key={item.vehicleContractId} hover>
                  <TableCell>{formatNotaryData(item.notary?.name, item.notary?.number)}</TableCell>
                  <TableCell>{item.registrationNumber ? item.registrationNumber : ""}</TableCell>
                  <TableCell>
                    {item.issueDate && item.issueDate.getFullYear() > 1900 ? dateToStringShort(item.issueDate) : ""}
                  </TableCell>
                  <TableCell>{item.caseURI}</TableCell>
                  <TableCell>{item.vehicleRegistrationNumber}</TableCell>
                  <TableCell>{item.vehicleIdentificationNumber}</TableCell>
                  <TableCell>
                    <Stack direction="row-reverse">
                      <Tooltip title={PublicRoutesConfig.Vehicle.Preview.Label}>
                        <IconButton
                          component={Link}
                          to={generatePath(PublicRoutesConfig.Vehicle.Preview.Path, {
                            id: item.vehicleContractId?.toString(),
                          })}
                        >
                          {PublicRoutesConfig.Vehicle.Preview.Icon}
                        </IconButton>
                      </Tooltip>
                      {item.isEditable && !form.getValues(nameof(defaultModel.isCancelled)) && (
                        <Tooltip title={PublicRoutesConfig.Vehicle.Edit.Label}>
                          <IconButton
                            component={Link}
                            to={generatePath(PublicRoutesConfig.Vehicle.Edit.Path, {
                              id: item.vehicleContractId?.toString(),
                            })}
                          >
                            {PublicRoutesConfig.Vehicle.Edit.Icon}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          count={form.pageCount}
          onChange={form.onPageChanged}
          page={form.watch(nameof(defaultModel.currentPage))}
        />
      </>
    ) : (
      form.searched && <AlertExtended type="warning">Няма намерени резултати.</AlertExtended>
    );
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Договори МПС</Typography>
      {renderForm()}
      {renderTable()}
    </Stack>
  );
}
